import { ServiceVariation } from "@/services/backend/omni/servicevariation/types/service-variation";
import t from "@/lang/lang";
import { PriceModifier } from "@/services/backend/omni/pricemodifier/types/price-modifier";
import { PositionCategory } from "./position-category";
import { UUID } from "@bitory-gmbh/nidavellir";
import { PriceUsed } from "./price-used";
import { PositionKind } from "./position-kind";
import { PositionUnit } from "./position-unit";

/** Mirrors `positions.Position`, backend/core/omni/domain/positions/position.go. */
export type Position = {
  id: UUID;
  parentId?: UUID;
  serviceObjectId: string;
  number: string;
  order: number;
  text: string;
  textShort: string;
  productNumber: string;
  kind: PositionKind;
  active: boolean;
  category: PositionCategory;
  serviceKind: string;
  serviceVariation: ServiceVariation;
  area: string;
  group: string;
  hours: number;
  hoursRate: number;
  count: number;
  unit: PositionUnit | "";
  unitPrice: number;
  cycle: number;
  basePrice: number;
  priceModifiers: PriceModifier[];
  finalPrice: number;
  subscriptionInterval: PositionSubscriptionInterval;
  subscriptionPrice: number;
  priceUsed: PriceUsed;
  dataSheetIds: UUID[];
};

/** Mirrors `positions.SubscriptionInterval`, backend/core/omni/domain/positions/subscription_interval.go. */
export const PositionSubscriptionInterval = {
  None: "",
  Day: "day",
  Week: "week",
  Month: "month",
  Quarter: "quarter",
  Year: "year",
};

export type PositionSubscriptionInterval =
  (typeof PositionSubscriptionInterval)[keyof typeof PositionSubscriptionInterval];

// A Radix SelectItem rejects the empty value, so no subscription is
// selected by this placeholder value instead.
const noSubscriptionSelectValue = "none";

export const PositionSubscriptionIntervalSelectItems = [
  { value: noSubscriptionSelectValue, label: t("Kein Abo") },
  { value: PositionSubscriptionInterval.Day, label: t("Tag") },
  { value: PositionSubscriptionInterval.Week, label: t("Woche") },
  { value: PositionSubscriptionInterval.Month, label: t("Monat") },
  { value: PositionSubscriptionInterval.Quarter, label: t("Quartal") },
  { value: PositionSubscriptionInterval.Year, label: t("Jahr") },
];

/**
 * subscriptionIntervalToSelectValue returns the value of interval in
 * {@link PositionSubscriptionIntervalSelectItems}.
 */
export function subscriptionIntervalToSelectValue(
  interval: PositionSubscriptionInterval,
): string {
  return interval === PositionSubscriptionInterval.None
    ? noSubscriptionSelectValue
    : interval;
}

/**
 * subscriptionIntervalFromSelectValue returns the interval of a value of
 * {@link PositionSubscriptionIntervalSelectItems}.
 */
export function subscriptionIntervalFromSelectValue(
  value: string,
): PositionSubscriptionInterval {
  return value === noSubscriptionSelectValue
    ? PositionSubscriptionInterval.None
    : value;
}

/** Mirrors `positions.Patch`, backend/core/omni/domain/positions/position.go. */
export type PositionPatch = {
  number?: string;
  order?: number;
  text?: string;
  textShort?: string;
  productNumber?: string;
  active?: boolean;
  category?: PositionCategory;
  serviceObjectId?: string;
  serviceKind?: string;
  serviceVariation?: ServiceVariation;
  area?: string;
  group?: string;
  hours?: number;
  hoursRate?: number;
  count?: number;
  unit?: PositionUnit | "";
  unitPrice?: number;
  cycle?: number;
  subscriptionInterval?: PositionSubscriptionInterval;
  priceUsed?: PriceUsed;
  dataSheetIds?: UUID[];
  priceModifiers?: PriceModifier[];
};

/**
 * distinct returns the distinct values of field of the positions, in the
 * order of their first occurrence. The empty value, grouping the positions
 * without a group or area, is only returned if includeEmpty is set.
 */
export function distinct(
  positions: Position[],
  field: "group" | "area",
  includeEmpty: boolean = true,
): string[] {
  const values = new Set(positions.map((p) => p[field]));
  if (!includeEmpty) {
    values.delete("");
  }

  return [...values];
}

/** PositionIdPatch is a {@link PositionPatch} of the position positionId. */
export type PositionIdPatch = PositionPatch & { positionId: string };

/**
 * reorderPatches returns the patches moving the position activeId to the
 * place of the position overId within positions, e.g. the rows of one
 * table section.
 *
 * A position moves together with its sub-positions, and a sub-position
 * only moves among its siblings, since the backend keeps every
 * sub-position directly below its parent. Sub-positions have no
 * sub-positions of their own. Dropping a position onto a sub-position of
 * another position targets that position. The positions are re-assigned their own
 * order values in the new sequence, so positions outside of them keep
 * their orders. Only positions whose order changes get a patch; none for
 * an unknown id, the same id or a drop outside the siblings. The total
 * always stays last: it can be neither dragged nor dropped onto.
 */
export function reorderPatches(
  positions: Position[],
  activeId: string,
  overId: string,
): PositionIdPatch[] {
  const sorted = [...positions].sort((a, b) => a.order - b.order);
  const byId = new Map(sorted.map((p) => [p.id, p]));
  const active = byId.get(activeId);
  const over = byId.get(overId);
  if (
    !active ||
    !over ||
    activeId === overId ||
    active.kind === PositionKind.Total ||
    over.kind === PositionKind.Total
  ) {
    return [];
  }

  // a parent missing from positions makes its sub-positions roots
  const parentOf = (p: Position) =>
    p.parentId && byId.has(p.parentId) ? p.parentId : "";
  const children = new Map<string, Position[]>();
  for (const p of sorted) {
    const siblings = children.get(parentOf(p)) ?? [];
    siblings.push(p);
    children.set(parentOf(p), siblings);
  }

  // the sibling of active that is overId or one of its ancestors
  const siblings = children.get(parentOf(active)) ?? [];
  let target = byId.get(overId);
  while (target && !siblings.includes(target)) {
    target = byId.get(parentOf(target));
  }
  if (!target || target === active) {
    return [];
  }

  const from = siblings.indexOf(active);
  const to = siblings.indexOf(target);
  siblings.splice(to, 0, ...siblings.splice(from, 1));

  const flattened: Position[] = [];
  const flatten = (parentId: string) => {
    for (const p of children.get(parentId) ?? []) {
      flattened.push(p);
      flatten(p.id);
    }
  };
  flatten("");

  const orders = sorted.map((p) => p.order);
  return flattened
    .map((p, i) => ({ positionId: p.id, order: orders[i] }))
    .filter((patch, i) => flattened[i].order !== patch.order);
}

/**
 * moveIntoGroupingPatches returns the patches of {@link reorderPatches}
 * and moves a top-level position into the grouping of its new place: it
 * takes over group and area of the row directly before it, a title or a
 * position, or none at the top. Sub-positions only reorder, their
 * grouping is that of their parent.
 */
export function moveIntoGroupingPatches(
  positions: Position[],
  activeId: string,
  overId: string,
): PositionIdPatch[] {
  const patches = reorderPatches(positions, activeId, overId);
  const active = positions.find((p) => p.id === activeId);
  if (patches.length === 0 || !active || active.parentId) {
    return patches;
  }

  const moved = patchPositions(positions, patches).sort(
    (a, b) => a.order - b.order,
  );
  const before = moved[moved.findIndex((p) => p.id === activeId) - 1];
  const group = before?.group ?? "";
  const area = before?.area ?? "";
  if (group === active.group && area === active.area) {
    return patches;
  }

  const grouping = { group, area };
  const activePatch = patches.find((p) => p.positionId === activeId);
  if (!activePatch) {
    return [...patches, { positionId: activeId, ...grouping }];
  }
  return patches.map((p) => (p === activePatch ? { ...p, ...grouping } : p));
}

/**
 * moveGroupingPatches returns the patches moving the block of the title
 * activeId, i.e. the title with all positions of its grouping, to the
 * place of the position overId. The positions of a block keep their
 * relative order, a sub-position stays with its parent and the total
 * stays last.
 *
 * - A block of the leading grouping, see {@link leadingGrouping}, moves
 *   among the top-level items: the other leading blocks and the
 *   positions outside of them. Its grouping is kept.
 * - An inner block, e.g. a room in a building, moves among the items of
 *   its leading block, whose title stays first. Dropped into another
 *   leading block, it takes over its leading value and merges with an
 *   inner block of the same name there.
 *
 * Positions are re-assigned the existing order values in the new
 * sequence, as in {@link reorderPatches}. There are no patches for a
 * drop onto the total, onto the block itself, or of an inner block onto
 * a position outside of any leading block.
 */
export function moveGroupingPatches(
  positions: Position[],
  activeId: string,
  overId: string,
): PositionIdPatch[] {
  const sorted = [...positions].sort((a, b) => a.order - b.order);
  const active = sorted.find((p) => p.id === activeId);
  const over = sorted.find((p) => p.id === overId);
  if (
    !active ||
    !over ||
    activeId === overId ||
    over.kind === PositionKind.Total ||
    (active.kind !== PositionKind.GroupTitle &&
      active.kind !== PositionKind.AreaTitle)
  ) {
    return [];
  }

  const field = active.kind === PositionKind.GroupTitle ? "group" : "area";
  const leader = leadingGrouping(positions) ?? field;
  const inner = leader === "group" ? "area" : "group";

  // groups the positions by key, in the order of first occurrence
  const unitsBy = (ps: Position[], key: (p: Position) => string) => {
    const units = new Map<string, Position[]>();
    for (const p of ps) {
      units.set(key(p), [...(units.get(key(p)) ?? []), p]);
    }
    return units;
  };
  const ownUnit = (p: Position) => `position:${p.parentId || p.id}`;
  const leadingKey = (p: Position) =>
    p[leader] ? `leading:${p[leader]}` : ownUnit(p);
  const innerKey = (p: Position) =>
    p.kind === positionTitleKind(leader) && !p[inner]
      ? "title"
      : p[inner]
        ? `inner:${p[inner]}`
        : ownUnit(p);
  // moves the unit from before or after the unit to, like a drag
  const move = (keys: string[], from: string, to: string) => {
    const result = [...keys];
    result.splice(
      result.indexOf(to),
      0,
      ...result.splice(result.indexOf(from), 1),
    );
    return result;
  };

  const totals = sorted.filter((p) => p.kind === PositionKind.Total);
  const units = unitsBy(
    sorted.filter((p) => p.kind !== PositionKind.Total),
    leadingKey,
  );
  let keys = [...units.keys()];
  let moved: Position[] = [];

  if (field === leader) {
    const from = leadingKey(active);
    const to = leadingKey(over);
    if (from === to) {
      return [];
    }
    keys = move(keys, from, to);
  } else {
    const from = `leading:${active[leader]}`;
    const to = over[leader] ? `leading:${over[leader]}` : "";
    const block = (units.get(from) ?? []).filter(
      (p) => p[inner] === active[inner],
    );
    if (!to || block.includes(over)) {
      return [];
    }

    const blockKey = `inner:${active[inner]}`;
    // the item of over in the target leading block; onto the title
    // means right after it, since the title stays first
    const overKeyIn = (keysOfTarget: string[]) =>
      innerKey(over) === "title"
        ? keysOfTarget[keysOfTarget.indexOf("title") + 1]
        : innerKey(over);

    if (from === to) {
      const own = unitsBy(units.get(from) ?? [], innerKey);
      const ownKeys = [...own.keys()];
      const toKey = overKeyIn(ownKeys);
      if (!toKey || toKey === blockKey) {
        return [];
      }
      units.set(
        from,
        move(ownKeys, blockKey, toKey).flatMap((k) => own.get(k) ?? []),
      );
    } else {
      const target = unitsBy(units.get(to) ?? [], innerKey);
      let targetKeys = [...target.keys()];
      if (target.has(blockKey)) {
        // merges with the inner block of the same name
        target.set(blockKey, [...target.get(blockKey)!, ...block]);
      } else {
        const toKey = overKeyIn(targetKeys);
        target.set(blockKey, block);
        const at = toKey ? targetKeys.indexOf(toKey) : targetKeys.length;
        targetKeys = [
          ...targetKeys.slice(0, at),
          blockKey,
          ...targetKeys.slice(at),
        ];
      }
      units.set(
        to,
        targetKeys.flatMap((k) => target.get(k) ?? []),
      );
      units.set(
        from,
        (units.get(from) ?? []).filter((p) => !block.includes(p)),
      );
      moved = block.filter((p) => !p.parentId);
    }
  }

  const flattened = [...keys.flatMap((k) => units.get(k) ?? []), ...totals];
  const orders = sorted.map((p) => p.order);
  return flattened
    .map((p, i) => {
      const patch: PositionIdPatch = { positionId: p.id };
      if (p.order !== orders[i]) {
        patch.order = orders[i];
      }
      if (moved.includes(p)) {
        patch[leader] = over[leader];
      }
      return patch;
    })
    .filter((patch) => Object.keys(patch).length > 1);
}

function positionTitleKind(field: "group" | "area"): PositionKind {
  return field === "group" ? PositionKind.GroupTitle : PositionKind.AreaTitle;
}

/**
 * leadingGrouping returns which of group and area leads, i.e. which one
 * the other is added within: the one used first. By order, the first
 * group or area title decides, without titles the first position with
 * only one of group and area. It is undefined while neither is used.
 * Mirrors `positions.LeadingGrouping`, backend/core/omni/domain/positions/grouping.go.
 */
export function leadingGrouping(
  positions: Position[],
): "group" | "area" | undefined {
  const sorted = [...positions].sort((a, b) => a.order - b.order);
  for (const p of sorted) {
    if (p.kind === PositionKind.GroupTitle) return "group";
    if (p.kind === PositionKind.AreaTitle) return "area";
  }
  for (const p of sorted) {
    if (p.group && !p.area) return "group";
    if (!p.group && p.area) return "area";
  }
  if (sorted.some((p) => p.group && p.area)) {
    return "group";
  }
  return undefined;
}

/**
 * patchPositions applies the patches to the positions they address, e.g.
 * for an optimistic cache update. Positions without a patch are returned
 * as they are.
 */
export function patchPositions(
  positions: Position[],
  patches: PositionIdPatch[],
): Position[] {
  return positions.map((p) => {
    const patch = patches.find((pp) => pp.positionId === p.id);
    if (!patch) {
      return p;
    }
    const { positionId: _, ...fields } = patch;
    return { ...p, ...fields };
  });
}
